import { ShareScreenBase } from "./share-screen-base"
import { RoomContentCard } from "./room-content-card"
import { getRoomDeeplinkUrl, isMobile, isUsOrCaTimezone } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { getInviterData, getRoomData, getTopMessages } from "~/share/share-embedded-data"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useRoomShareEmailSubmission } from "~/hooks/use-email-submission"
import { useCollectEmail } from "~/hooks/use-collect-email"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"
import { TopMessagesSection } from "./components/top-messages-section"
import { PresentedBy } from "./components/room-content-card/presented-by"

export const ShareRoomScreen = (): JSX.Element => {
  const roomData = getRoomData()
  const topMessages = getTopMessages()
  const collectEmail = useCollectEmail()

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getRoomDeeplinkUrl(appEnvironment, roomData.id)
  const roomTitle = [roomData.name, "on ChatBCC"].join(" ")
  useDocumentTitleDescription(roomTitle, roomData.description ?? null)

  const phoneSubmission = useRoomSharePhoneSubmission()
  const emailSubmission = useRoomShareEmailSubmission()

  const roomId = roomData.id
  const roomHandle = roomData.roomHandle ?? undefined

  useScreenTracking(SCREENS.Share, {
    room_id: roomId,
    room_handle: roomHandle,
    deeplink_url: deeplinkUrl,
    ad_id: roomData.sponsorCreative?.id,
  })

  const handleJoinRoom = useCallback(async () => {
    const successMessage =
      isMobile() || !isUsOrCaTimezone()
        ? "Download the app to join the group chat."
        : collectEmail
          ? "Check your email for a link to download the app."
          : "Check your texts for a link to download the app."

    const successTitle = "You skipped the waitlist!"
    const inviterData = getInviterData()

    const trackingData = {
      unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
      inviter_id: inviterData?.id,
      inviter_username: inviterData?.username,
      room_id: roomId,
      deeplink_url: deeplinkUrl,
      room_handle: roomHandle,
    }

    if (collectEmail) {
      await emailSubmission.submitEmail({
        successMessage,
        successTitle,
        roomId,
        trackingData,
      })
    } else {
      await phoneSubmission.submitPhoneNumber({
        successMessage,
        successTitle,
        roomId,
        trackingData,
      })
    }
  }, [collectEmail, emailSubmission, phoneSubmission, deeplinkUrl, roomHandle, roomId])

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      deeplinkUrl={deeplinkUrl}
      handleJoinRoom={handleJoinRoom}
      analyticsProps={{
        room_id: roomId,
        room_handle: roomHandle,
      }}
      {...inputProps}
      renderContent={() =>
        roomData && (
          <div className="flex w-full max-w-[640px] flex-col">
            <RoomContentCard roomData={roomData} compact={!!topMessages} />
            <PresentedBy roomData={roomData} />
            {topMessages && <TopMessagesSection messages={topMessages} />}
          </div>
        )
      }
    />
  )
}

export default ShareRoomScreen
